import { t, subscribeUiLocale } from '@/i18n/locale';
import { matchPath } from 'react-router-dom';

type AppSurface = 'learn' | 'portal' | 'teacher' | 'public';

interface PageTitleRule {
  path: string;
  label: string;
  surface: AppSurface;
}

const PAGE_TITLE_RULES: PageTitleRule[] = [
  { path: '/labs/physics', label: 'Physics Lab', surface: 'public' },
  { path: '/labs/chemistry', label: 'Virtual Chemistry Lab', surface: 'public' },
  { path: '/portal/study', label: 'Family Study', surface: 'portal' },
  { path: '/play/:shareId', label: 'Play project', surface: 'public' },
  { path: '/try/playground', label: 'Try Creative Code Studio', surface: 'public' },
  { path: '/try/blocks', label: 'Try Story Blocks', surface: 'public' },
  { path: '/try/art', label: 'Try Art Studio', surface: 'public' },
  { path: '/try/music', label: 'Try Music Stage', surface: 'public' },
  { path: '/try/video', label: 'Try Video Studio', surface: 'public' },
  { path: '/try/sibling-discount', label: 'Sibling discount example', surface: 'public' },
  { path: '/vote/:slug', label: 'Creative Code Challenge', surface: 'public' },
  { path: '/challenge/:slug/showcase', label: 'Creator Showcase', surface: 'public' },

  // Parent Portal: specific routes must come before their parent route.
  { path: '/portal/register', label: 'Create account', surface: 'portal' },
  { path: '/portal/family-link/:token', label: 'Family invite', surface: 'portal' },
  { path: '/portal/login', label: 'Sign in', surface: 'portal' },
  { path: '/portal/courses/:courseSlug', label: 'Course details', surface: 'portal' },
  { path: '/portal/courses', label: 'Courses', surface: 'portal' },
  { path: '/portal/tutoring/:slug', label: 'Tutor profile', surface: 'portal' },
  { path: '/portal/tutoring', label: 'Tutoring', surface: 'portal' },
  { path: '/portal/teachers/:slug', label: 'Tutor profile', surface: 'portal' },
  { path: '/portal/teachers', label: 'Tutoring', surface: 'portal' },
  { path: '/portal/academy/hsc-planner', label: 'HSC Planner', surface: 'portal' },
  { path: '/portal/academy/products/:slug', label: 'Exam prep details', surface: 'portal' },
  { path: '/portal/academy/checkout/:sku', label: 'Exam prep checkout', surface: 'portal' },
  { path: '/portal/academy/orders/:intentId', label: 'Exam prep order', surface: 'portal' },
  { path: '/portal/academy/reports/:sessionId', label: 'Exam prep report', surface: 'portal' },
  { path: '/portal/academy', label: 'Exam Prep', surface: 'portal' },
  { path: '/portal/self-paced/:slug', label: 'Online course', surface: 'portal' },
  { path: '/portal/self-paced', label: 'Online courses', surface: 'portal' },
  { path: '/portal/checkout/class/:classId', label: 'Course checkout', surface: 'portal' },
  { path: '/portal/challenge/:slug/register', label: 'Challenge registration', surface: 'portal' },
  { path: '/portal/challenge/:slug/report', label: 'Challenge report', surface: 'portal' },
  { path: '/portal/challenge/:slug', label: 'Creative Code Challenge', surface: 'portal' },
  { path: '/portal/family/new', label: 'Add a kid', surface: 'portal' },
  { path: '/portal/family/:kidId/projects', label: 'Kid projects', surface: 'portal' },
  { path: '/portal/family/:kidId/passport', label: 'Creator Passport', surface: 'portal' },
  { path: '/portal/family/:kidId/images', label: 'Kid pictures', surface: 'portal' },
  { path: '/portal/family/:kidId/settings', label: 'Kid settings', surface: 'portal' },
  { path: '/portal/family/:kidId/study', label: 'Learning Support Report', surface: 'portal' },
  { path: '/portal/family/:kidId', label: 'Activities', surface: 'portal' },
  { path: '/portal/projects', label: 'My Projects', surface: 'portal' },
  { path: '/portal/wallet/auto-topup', label: 'Auto top-up', surface: 'portal' },
  { path: '/portal/wallet/stars-guide', label: 'How Stars work', surface: 'portal' },
  { path: '/portal/wallet/topup', label: 'Top up Stars', surface: 'portal' },
  { path: '/portal/wallet', label: 'Wallet and limits', surface: 'portal' },
  { path: '/portal/usage/:kidId', label: 'Star usage', surface: 'portal' },
  { path: '/portal/usage', label: 'Star usage', surface: 'portal' },
  { path: '/portal/audit/project/:id', label: 'Project activity', surface: 'portal' },
  { path: '/portal/classes', label: 'Courses', surface: 'portal' },
  { path: '/portal/explore', label: 'Explore', surface: 'portal' },
  { path: '/portal/gallery', label: 'Student Work', surface: 'portal' },
  { path: '/portal/more', label: 'More', surface: 'portal' },
  { path: '/portal/guides', label: 'Family Guides', surface: 'portal' },
  { path: '/portal/approvals', label: 'Notifications', surface: 'portal' },
  { path: '/portal/settings', label: 'Settings', surface: 'portal' },
  { path: '/portal/billing', label: 'Payment and billing', surface: 'portal' },
  { path: '/portal/dashboard', label: 'Dashboard', surface: 'portal' },
  { path: '/portal', label: 'Dashboard', surface: 'portal' },

  { path: '/learn/companion/design', label: 'Dress my buddy', surface: 'learn' },
  { path: '/learn/companion', label: 'My buddy', surface: 'learn' },
  {
    path: '/portal/family/:kidId/companion',
    label: 'Your child’s companion memories',
    surface: 'portal',
  },
  // Learn routes
  { path: '/learn/login', label: 'Student sign in', surface: 'learn' },
  { path: '/learn/signup', label: 'Student sign up', surface: 'learn' },
  { path: '/learn/class-code', label: 'Join a class', surface: 'learn' },
  { path: '/learn/temporary', label: 'Temporary student sign in', surface: 'learn' },
  { path: '/learn/handoff', label: 'Open Learn', surface: 'learn' },
  { path: '/learn/projects/new', label: 'New project', surface: 'learn' },
  { path: '/learn/projects/:id', label: 'Project details', surface: 'learn' },
  { path: '/learn/projects', label: 'Projects', surface: 'learn' },
  { path: '/learn/passport', label: 'Creator Passport', surface: 'learn' },
  { path: '/learn/missions/:id', label: 'Lesson', surface: 'learn' },
  { path: '/learn/missions', label: 'Lessons', surface: 'learn' },
  { path: '/learn/classroom/:classId/post/:projectId', label: 'Class post', surface: 'learn' },
  { path: '/learn/classroom/:classId/games', label: 'Class games', surface: 'learn' },
  { path: '/learn/classroom/:classId', label: 'Class', surface: 'learn' },
  { path: '/learn/classroom', label: 'My Classes', surface: 'learn' },
  { path: '/learn/create/image/canvas', label: 'Art Studio', surface: 'learn' },
  { path: '/learn/create/image', label: 'Drawing ideas', surface: 'learn' },
  { path: '/learn/create/voice', label: 'Voice Booth', surface: 'learn' },
  { path: '/learn/create/video', label: 'AI Video Studio', surface: 'learn' },
  { path: '/learn/video/:projectId', label: 'AI Video Studio', surface: 'learn' },
  { path: '/learn/create/code', label: 'Code Studio', surface: 'learn' },
  { path: '/learn/create/blocks', label: 'Story Blocks', surface: 'learn' },
  { path: '/learn/create', label: 'Create', surface: 'learn' },
  { path: '/learn/gallery', label: 'Student Work', surface: 'learn' },
  { path: '/learn/courses/:slug', label: 'Online course', surface: 'learn' },
  { path: '/learn/courses', label: 'Online courses', surface: 'learn' },
  { path: '/learn/blocks/:projectId', label: 'Story Blocks', surface: 'learn' },
  { path: '/learn/story/journey-west/:partId', label: 'Journey to the West', surface: 'learn' },
  { path: '/learn/story/journey-west', label: 'Journey to the West', surface: 'learn' },
  { path: '/learn/code/:projectId/run', label: 'Run project', surface: 'learn' },
  { path: '/learn/code/:projectId', label: 'Code Studio', surface: 'learn' },
  { path: '/learn/playground/:projectId', label: 'Creative Code Studio', surface: 'learn' },
  { path: '/learn/music/:sessionId', label: 'Music Stage', surface: 'learn' },
  { path: '/learn/music', label: 'Music Stage', surface: 'learn' },
  { path: '/learn/exams/:productSlug/mock/:paperId', label: 'Mock exam', surface: 'learn' },
  { path: '/learn/exams/:productSlug/practice', label: 'Exam practice', surface: 'learn' },
  { path: '/learn/exams/:productSlug', label: 'Exam prep', surface: 'learn' },
  { path: '/learn/exams', label: 'My Exam Prep', surface: 'learn' },
  { path: '/learn/challenge/:slug/submit', label: 'Challenge submission', surface: 'learn' },
  { path: '/learn/workspace', label: 'AI Studio', surface: 'learn' },
  { path: '/learn/profile/stars', label: 'Top up Stars', surface: 'learn' },
  // Independent student account (D-SSU-23): the Portal twins under /learn/account.
  { path: '/learn/account/wallet/auto-topup', label: 'Auto top-up', surface: 'learn' },
  { path: '/learn/account/wallet/stars-guide', label: 'How Stars work', surface: 'learn' },
  { path: '/learn/account/wallet/topup', label: 'Top up Stars', surface: 'learn' },
  { path: '/learn/account/wallet', label: 'Wallet and limits', surface: 'learn' },
  { path: '/learn/account/billing', label: 'Orders and receipts', surface: 'learn' },
  { path: '/learn/account/usage/:kidId', label: 'Star usage', surface: 'learn' },
  { path: '/learn/account/usage', label: 'Star usage', surface: 'learn' },
  { path: '/learn/account/settings', label: 'Data and privacy', surface: 'learn' },
  { path: '/learn/account/courses/:courseSlug', label: 'Course details', surface: 'learn' },
  { path: '/learn/account/courses', label: 'Courses', surface: 'learn' },
  { path: '/learn/account/checkout/class/:classId', label: 'Course checkout', surface: 'learn' },
  { path: '/learn/account/tutoring/:slug', label: 'Tutor profile', surface: 'learn' },
  { path: '/learn/account/tutoring', label: 'Tutoring', surface: 'learn' },
  { path: '/learn/account/academy/hsc-planner', label: 'HSC Planner', surface: 'learn' },
  { path: '/learn/account/academy/products/:slug', label: 'Exam prep details', surface: 'learn' },
  { path: '/learn/account/academy/checkout/:sku', label: 'Exam prep checkout', surface: 'learn' },
  { path: '/learn/account/academy/orders/:intentId', label: 'Exam prep order', surface: 'learn' },
  {
    path: '/learn/account/academy/reports/:sessionId',
    label: 'Exam prep report',
    surface: 'learn',
  },
  { path: '/learn/account/academy', label: 'Exam Prep', surface: 'learn' },
  { path: '/learn/account/challenge/:slug/register', label: 'Challenge entry', surface: 'learn' },
  { path: '/learn/account/challenge/:slug/report', label: 'Challenge report', surface: 'learn' },
  { path: '/learn/account/challenge/:slug', label: 'Creative Code Challenge', surface: 'learn' },
  { path: '/learn/account/study', label: 'Study access', surface: 'learn' },
  { path: '/learn/account/family/:kidId/study', label: 'Study report', surface: 'learn' },
  { path: '/learn/account/pathway', label: 'Learning pathway', surface: 'learn' },
  { path: '/learn/account/guides', label: 'Guides', surface: 'learn' },
  { path: '/learn/account', label: 'My account', surface: 'learn' },
  { path: '/learn/profile', label: 'My profile', surface: 'learn' },
  { path: '/learn/hsc', label: 'HSC Plan', surface: 'learn' },
  { path: '/learn', label: 'Home', surface: 'learn' },

  // Teacher class-session routes
  {
    path: '/teacher/classes/:classId/kids/:kidId/assessment',
    label: 'Assessment',
    surface: 'teacher',
  },
  { path: '/teacher/classes/:classId/kids/:kidId', label: 'Student live view', surface: 'teacher' },
  { path: '/teacher/classes/:classId', label: 'Class dashboard', surface: 'teacher' },
  { path: '/teacher/projects/:projectId/live', label: 'Live project', surface: 'teacher' },
  { path: '/teacher/prep/:projectId', label: 'Prep project', surface: 'teacher' },
];

const SURFACE_SUFFIX: Record<AppSurface, string> = {
  learn: 'Airbotix Learn',
  portal: 'Airbotix Parent Portal',
  teacher: 'Airbotix Teacher',
  public: 'Airbotix',
};

function fallbackTitle(pathname: string): string {
  if (pathname.startsWith('/learn')) return t('Airbotix Learn');
  if (pathname.startsWith('/portal')) return t('Airbotix Parent Portal');
  if (pathname.startsWith('/teacher')) return 'Airbotix Teacher';
  return 'Airbotix';
}

export function getPageTitle(pathname: string): string {
  const rule = PAGE_TITLE_RULES.find(({ path }) => matchPath({ path, end: true }, pathname));
  return rule ? `${t(rule.label)} · ${t(SURFACE_SUFFIX[rule.surface])}` : fallbackTitle(pathname);
}

interface TitleRouter {
  state: { location: { pathname: string } };
  subscribe: (listener: (state: { location: { pathname: string } }) => void) => () => void;
}

/** Keep the browser tab in sync with the current SPA route. */
export function startPageTitleTracking(router: TitleRouter): () => void {
  let previousPathname = router.state.location.pathname;
  document.title = getPageTitle(previousPathname);

  const language = subscribeUiLocale(() => {
    document.title = getPageTitle(previousPathname);
  });
  const route = router.subscribe((state) => {
    const { pathname } = state.location;
    if (pathname === previousPathname) return;
    previousPathname = pathname;
    document.title = getPageTitle(pathname);
  });
  return () => {
    language();
    route();
  };
}
